import i18n from 'i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import { CHART_FONT_STYLE } from '@/chart/chart.constants';
import { sqWorkbenchStore, sqWorksheetStore } from '@/core/core.stores';
import { TIME_FORMAT_NUMERICAL_SHORT } from '@/datetime/dateTime.utilities';
import {
  BOXPLOT_LABEL_Z_INDEX,
  CHARTS_ORDER,
  CHART_TABLE_COL_NAME,
  COMPARISON_BASELINE_VALUE,
  COMPARISON_NO_VALUE,
  CURSOR_Y_OFFSET,
  RUN_RULES_MARKER_MAP,
  SCATTER_DOT_OVERLAY_RADIUS,
  SCATTER_DOT_RADIUS,
  Y_AXIS_LABEL,
} from '@/summaryReport/summaryData.constants';
import { ALIGN_WEEKLY_ID } from '@/tools/statisticsSummary/statisticsSummary.constants';
import { sqTimezones } from '@/utilities/datetime.constants';
import { isForbidden } from '@/utilities/http.utilities';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type {
  BoxPlotPoint,
  ChartKeys,
  ChartPoint,
  OutlierBin,
  OutlierPoint,
  SpcChart,
  StatisticsSummaryItem,
} from '@/summaryReport/summaryData.types';
import type { SummaryReportActions } from '@/summaryReport/summaryReport.store';
import type { Cursor, CursorPoint } from '@/utilities/cursor.constants';

export const truncateValue = (value: number, precision = 4): number => {
  if (!Number.isFinite(value) || Number.isInteger(value)) return value;
  const absValue = Math.abs(value);
  if (absValue < 1 && absValue > 0) {
    return parseFloat(value.toFixed(precision));
  } else {
    return parseFloat(value.toPrecision(precision));
  }
};

export function getExponent(n: number) {
  if (!Number.isFinite(n)) return NaN;
  if (n === 0) return 0;
  return Number(n.toExponential().split('e')[1]);
}

export const getPropertyItems = (
  property: string,
  items: StatisticsSummaryItem[],
  baselineId = '',
  isComparison = false,
) => {
  const propertyMap: Record<string, string | number | undefined> = { statistic: property, id: property };
  items.forEach((item: StatisticsSummaryItem) => {
    if (isComparison) {
      propertyMap[item.name] =
        baselineId === item.id
          ? COMPARISON_BASELINE_VALUE
          : truncateValue(_.get(item.comparisonData, property, COMPARISON_NO_VALUE));
    } else {
      propertyMap[item.name] = truncateValue(_.get(item.data.stats, property, undefined));
    }
  });
  return propertyMap;
};

export const hasNoData = (item: StatisticsSummaryItem): boolean => !item.data?.stats || item.data.stats.count === 0;

export const hasError = (item: StatisticsSummaryItem): boolean => !!item.error?.message;

type BackendErrorData = { statusMessage?: string; message?: string };

/**
 * Extracts the best displayable message from any error shape.
 */
export function extractErrorMessage(error: unknown): string {
  const maybeError = error as {
    data?: BackendErrorData;
    response?: { data?: BackendErrorData };
  };
  const data = maybeError?.response?.data ?? maybeError?.data;
  const backendMessage = data?.statusMessage || data?.message;
  if (backendMessage) {
    return backendMessage;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return i18n.t(
    isForbidden(error)
      ? 'INVESTIGATE_TOOLS.SUMMARY_REPORT.REDACTED_INPUT'
      : 'INVESTIGATE_TOOLS.SUMMARY_REPORT.UNKNOWN_ERROR',
  );
}

/**
 * An item the user cannot access, in the minimum shape {@link formatRedactedInputsMessage} needs. `itemType`
 * is optional because only a probe of the item's own properties reveals it.
 */
export interface DeniedItem {
  itemId: string;
  itemType?: string;
  message: string;
}

export interface RedactedInputError extends DeniedItem {
  itemType: string;
}

/**
 * Identifies which of a statistics summary's input items the current user cannot access, along with the
 * backend's access-denied message for each ("Identity ... does not have access for the ..."). The backend
 * fails fast on the first inaccessible dependency, so each input is probed directly to build the complete
 * list. Throws when the summary itself cannot be fetched (forbidden, deleted, server error).
 */
export async function findRedactedStatsSummaryInputErrors(summaryId: string): Promise<RedactedInputError[]> {
  const { data } = await sqFormulasApi.getItem({ id: summaryId });
  const inputs = _.uniqBy(_.compact(_.map(data.parameters, (parameter) => parameter.item)), 'id');
  const probed = await Promise.all(
    inputs.map((input) =>
      sqItemsApi
        .getItemAndAllProperties({ id: input.id })
        .then(() => null)
        .catch((error) =>
          isForbidden(error) ? { itemId: input.id, itemType: input.type, message: extractErrorMessage(error) } : null,
        ),
    ),
  );
  return _.compact(probed);
}

/**
 * Formats denied items in the backend's access-error style. All requests run as the signed-in user, so for
 * multiple items the identity is stated once rather than repeated per item.
 */
export function formatRedactedInputsMessage(redactedErrors: DeniedItem[]): string {
  if (redactedErrors.length === 1) {
    return redactedErrors[0].message;
  }
  return i18n.t('INVESTIGATE_TOOLS.SUMMARY_REPORT.REDACTED_INPUTS', {
    userName: sqWorkbenchStore.currentUser.username,
    userId: sqWorkbenchStore.currentUser.id,
    items: redactedErrors.map(({ itemType, itemId }) => (itemType ? `${itemType} ${itemId}` : itemId)).join(', '),
  });
}

/**
 * Builds the user-facing error for a statistics summary data-load failure.
 *
 * A forbidden error from the backend names only the first inaccessible item, so the summary's inputs are
 * probed to build a message that names all of them. If the probe shows the summary itself is unreadable,
 * `summaryRedacted` is set so the tile can render its no-access state instead. Any other failure
 * (non-forbidden, or a 403 from an item the probe cannot see, such as a transitive dependency) just gets
 * the extracted message.
 */
export async function buildStatsSummaryError(
  summaryId: string,
  error: unknown,
): Promise<{ message: string; summaryRedacted?: boolean }> {
  if (isForbidden(error)) {
    try {
      const redactedErrors = await findRedactedStatsSummaryInputErrors(summaryId);
      if (redactedErrors.length > 0) {
        return { message: formatRedactedInputsMessage(redactedErrors) };
      }
    } catch (probeError) {
      if (isForbidden(probeError)) {
        return { message: extractErrorMessage(error), summaryRedacted: true };
      }
    }
  }
  return { message: extractErrorMessage(error) };
}

export const adjustColor = (color: string, percent = 50, lighten = true): string => {
  const num = parseInt(color.replace('#', ''), 16);

  if (lighten) {
    const r = Math.min(255, Math.floor((num >> 16) + (255 - (num >> 16)) * (percent / 100)));
    const g = Math.min(255, Math.floor(((num >> 8) & 0x00ff) + (255 - ((num >> 8) & 0x00ff)) * (percent / 100)));
    const b = Math.min(255, Math.floor((num & 0x0000ff) + (255 - (num & 0x0000ff)) * (percent / 100)));

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  } else {
    const r = Math.max(0, Math.floor((num >> 16) * (1 - percent / 100)));
    const g = Math.max(0, Math.floor(((num >> 8) & 0x00ff) * (1 - percent / 100)));
    const b = Math.max(0, Math.floor((num & 0x0000ff) * (1 - percent / 100)));

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }
};

export const calculateY = (x: number, mean: number, stdDev: number): number => {
  const coefficient = 1 / (stdDev * Math.sqrt(2 * Math.PI));
  const exponent = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
  return coefficient * Math.exp(exponent);
};

export const NUM_BELL_CURVE_POINTS = 81;

export const generatePoints = (
  average: number,
  stdDev: number,
  lower: number,
  upper: number,
  count: number,
  size = 1,
  yPlotUnit: Y_AXIS_LABEL = Y_AXIS_LABEL.COUNT,
): { x: number; y: number }[] => {
  if (count === 0) return [];
  const start = lower - size;
  const end = upper + size;
  const step = (end - start) / NUM_BELL_CURVE_POINTS;
  const points = _.range(start, end, step);
  return points.map((x) => {
    let y = calculateY(x, average, stdDev);
    if (yPlotUnit === Y_AXIS_LABEL.COUNT) {
      y = y * count * size;
    } else if (yPlotUnit === Y_AXIS_LABEL.PERCENTAGE) {
      y = count !== 0 ? y * size * 100 : 0;
    }
    return { x, y };
  });
};

/**
 * Fetches statistics summary data for a given item ID.
 *
 * @param id - The unique identifier of the statistics summary item
 * @returns A promise that resolves to the statistics summary data containing histogram, stats, inputs, unit of
 * measurement, and uncertainty flag
 */
export const fetchStatsSummaryData = async (id: string) => {
  return await sqFormulasApi.statsSummary({ formula: '$stats', parameters: [`stats=${id}`] });
};

/**
 * Compares two statistics summaries and returns comparison test results.
 *
 * @param baselineId - The ID of the baseline stat summary to compare against
 * @param statsSummaryId - The ID of the test stat summary to compare
 * @returns Promise containing comparison data with test statistics and results.
 */
export async function compareSummaries(baselineId: string, statsSummaryId: string) {
  const formula = 'compareSummaries($baseline, $summary)';
  return await sqFormulasApi.compareStatsSummaries({
    formula,
    parameters: [`baseline=${baselineId}`, `summary=${statsSummaryId}`],
  });
}

/**
 * Extracts the setMaximumDuration value from a formula string for a given parameter.
 * Example: "$alignCondition.setMaximumDuration(40h)" returns "40h"
 *
 * @param formula - The formula string to parse
 * @param paramName - The parameter name to look for (e.g., "alignmentCondition")
 * @returns The duration string (e.g., "40h") or undefined if not found
 */
function extractMaxDurationFromFormula(formula: string, paramName: string): string | undefined {
  // Match patterns like: $paramName.setMaximumDuration(40h) or $paramName.setMaximumDuration(1.5days)
  const regex = new RegExp(`\\$${paramName}\\.setMaximumDuration\\(([^)]+)\\)`, 'i');
  const match = formula.match(regex);
  return match?.[1];
}

/**
 * Get the table data for the stats summary.
 *
 * @param statsSummaryId - The ID of the statistics summary item to get the table data for
 * @param signalId - The ID of the signal item associated with the statistics summary
 * @param filteringConditionId - The ID of the filtering condition item (optional filter for all chart types)
 * @param alignmentConditionId - The ID of the alignment condition item (required for subgroup charts like XBAR)
 * @param alignmentConditionMetadata - Optional metadata for the alignment condition (used to check if max duration is
 *   set)
 * @param statsSummaryFormula - The formula used to create the stats summary (used to extract max duration if needed)
 * @returns Promise containing table data for the specified statistics summary and signal
 */
export async function fetchSpcChartTable(
  statsSummaryId: string,
  signalId: string,
  filteringConditionId?: string,
  alignmentConditionId?: string,
  alignmentConditionMetadata?: { maximumDuration?: { value?: number | null; uom?: string | null } },
  statsSummaryFormula?: string,
) {
  const parameters = [`stats=${statsSummaryId}`, `signal=${signalId}`];
  // CRAB-57943: toSpcChartTable requires the continuous $signal — not discretized and not pre-filtered.
  // The filter and align conditions are passed as separate arguments so the operator handles capsule alignment internally.
  let signalFormula = '$signal';

  const isWeekly = alignmentConditionId === ALIGN_WEEKLY_ID;

  // Add filter condition as first optional parameter
  if (filteringConditionId) {
    signalFormula += ', $filterCondition';
    parameters.push(`filterCondition=${filteringConditionId}`);
  }

  // Add alignment condition as second optional parameter
  if (alignmentConditionId && !isWeekly) {
    const hasMaxDuration = alignmentConditionMetadata?.maximumDuration?.value != null;

    // If the condition doesn't have a max duration, -extract it from the stats summary formula
    let alignmentConditionStr = '$alignCondition';
    if (!hasMaxDuration && statsSummaryFormula) {
      const extractedDuration = extractMaxDurationFromFormula(statsSummaryFormula, 'alignmentCondition');
      if (extractedDuration) {
        alignmentConditionStr = `$alignCondition.setMaximumDuration(${extractedDuration})`;
      }
    }

    signalFormula += `, ${alignmentConditionStr}`;
    parameters.push(`alignCondition=${alignmentConditionId}`);
  }

  const alignmentConditionFormula = isWeekly ? `, weeks(Day.Sunday, "${sqWorksheetStore.timezone.name}")` : '';

  const formula = `$stats.toSpcChartTable(${signalFormula}${alignmentConditionFormula})`;
  const { data } = await sqFormulasApi.runFormula({
    formula,
    parameters,
  });
  return data;
}

export function destroySVGElement(element: Highcharts.SVGElement | undefined) {
  element?.destroy();
}

function renderText(
  chart: Highcharts.Chart,
  text: string,
  xPixel = 0,
  yPixel = 0,
  color = 'black',
  fontSize = 'x-small',
) {
  return chart.renderer
    .text(text, xPixel, yPixel)
    .attr({
      zIndex: BOXPLOT_LABEL_Z_INDEX,
      padding: 2,
    })
    .css({
      ...CHART_FONT_STYLE,
      fontWeight: 'bold',
      fontSize,
      color,
    })
    .add();
}

const MIN_WHISKER_LABEL = 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER';
const MAX_WHISKER_LABEL = 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER';
const DEFAULT_LABEL_FONT_SIZE = 11;

type MeasuredLabel = { point: BoxPlotPoint; translatedText: string; center: number; fullWidth: number };
type Placement = { point: BoxPlotPoint; translatedText: string; leftEdge: number; row: number };

function packLabels(labels: MeasuredLabel[], scale: number): { placements: Placement[]; rowsUsed: number } {
  const edges = labels.map((label) => {
    const width = label.fullWidth * scale;
    const leftEdge = label.center - width / 2;
    return { ...label, leftEdge, rightEdge: leftEdge + width };
  });

  const minLabel = edges.find((label) => label.point.labelText === MIN_WHISKER_LABEL);
  const maxLabel = edges.find((label) => label.point.labelText === MAX_WHISKER_LABEL);
  const maxPairsWithMin = !!minLabel && !!maxLabel && maxLabel.leftEdge > minLabel.rightEdge;
  const rowCeiling = maxPairsWithMin ? maxLabel!.leftEdge : Infinity;

  const rowRightEdges: number[] = [];
  const placements = edges.map(({ point, translatedText, leftEdge, rightEdge }) => {
    let row =
      point.labelText === MAX_WHISKER_LABEL && maxPairsWithMin
        ? 0
        : rowRightEdges.findIndex(
            (occupiedUntil, idx) => leftEdge > occupiedUntil && !(idx === 0 && rightEdge >= rowCeiling),
          );
    if (row === -1) {
      row = rowRightEdges.length;
    }
    rowRightEdges[row] = rightEdge;
    return { point, translatedText, leftEdge, row };
  });

  const rowsUsed = placements.length ? Math.max(...placements.map((placement) => placement.row)) + 1 : 1;
  return { placements, rowsUsed };
}

export function drawPointLabel(
  chart: Highcharts.Chart,
  points: BoxPlotPoint[],
  seriesIndex: number,
  baseOffset: number,
  availableHeight: number,
) {
  let baseFontSize = DEFAULT_LABEL_FONT_SIZE;
  const labels: MeasuredLabel[] = points
    .filter((point) => _.isFinite(point.yPixel) && point.labelText)
    .map((point) => {
      const translatedText = i18n.t(point.labelText);
      const tempText = renderText(chart, translatedText);
      const fullWidth = tempText.getBBox().width;
      if (tempText.element) {
        const measuredFontSize = parseFloat(getComputedStyle(tempText.element).fontSize);
        if (Number.isFinite(measuredFontSize)) {
          baseFontSize = measuredFontSize;
        }
      }
      tempText.destroy();
      const center = chart.yAxis[0].toPixels(point.xValue);
      return { point, translatedText, center, fullWidth };
    })
    .sort((a, b) => a.center - b.center);

  let rowHeight = CURSOR_Y_OFFSET;
  let placements: Placement[] = [];
  for (let candidateRows = 1; candidateRows <= Math.max(labels.length, 1); candidateRows++) {
    const rawRowHeight = availableHeight / candidateRows;
    const candidateRowHeight = Number.isFinite(rawRowHeight)
      ? Math.max(1, Math.min(CURSOR_Y_OFFSET, rawRowHeight))
      : CURSOR_Y_OFFSET;
    const result = packLabels(labels, candidateRowHeight / CURSOR_Y_OFFSET);
    if (result.rowsUsed <= candidateRows) {
      rowHeight = candidateRowHeight;
      placements = result.placements;
      break;
    }
  }

  const fontSize = baseFontSize * (rowHeight / CURSOR_Y_OFFSET);

  placements.forEach(({ point, translatedText, leftEdge, row }) => {
    const labelText = `<span style="border-color: ${point.color}">${translatedText}</span>`;
    const yPixel = chart.xAxis[0].toPixels(seriesIndex) + baseOffset + rowHeight * (row + 1);
    point.label = renderText(chart, labelText, leftEdge, yPixel, point.color, `${fontSize}px`);
  });
  return points;
}

function createPointData(property: string, value: number, unit: string, color: string) {
  return {
    label: undefined,
    xValue: value,
    yPixel: value,
    color,
    labelText: property,
    unitOfMeasure: { value: unit, fromSourceUom: false, isDisplay: false },
  };
}

const getBoxPlotData = (item: StatisticsSummaryItem) => {
  if (item?.data) {
    return [
      {
        name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER',
        value: item.data.stats.minWhisker,
      },
      { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q1', value: item.data.stats.quartile1 },
      { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MEDIAN', value: item.data.stats.quartile2 },
      { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q3', value: item.data.stats.quartile3 },
      {
        name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER',
        value: item.data.stats.maxWhisker,
      },
    ];
  }
  return [
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q1', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q2', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q3', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER', value: 0 },
  ];
};

export function getLabelPoints(items: StatisticsSummaryItem[]) {
  const pointMap: Record<string, BoxPlotPoint[]> = {};
  items.forEach((item) => {
    pointMap[item.id] = getBoxPlotData(item).map((d) => createPointData(d.name, d.value, item.data.uom, item.color));
  });
  return pointMap;
}

export const getBoxWidth = (chart: Highcharts.Chart, idx: number) => {
  const boxplotSeries = chart.series.find((series) => series.type === 'boxplot');
  return boxplotSeries?.points[idx]?.shapeArgs?.width ?? 0;
};

export function binValues(values: OutlierPoint[], binWidth: number): OutlierBin[] {
  if (!values || values.length === 0 || binWidth <= 0) return [];

  const numericValues = values.map((v) => v.value);
  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);

  const bins: OutlierBin[] = [];
  for (let start = min; start <= max; start += binWidth) {
    const binned = values.filter((v) => v.value >= start && v.value < start + binWidth);
    if (binned.length > 0) {
      bins.push({ binStart: start, binEnd: start + binWidth, values: binned });
    }
  }
  return bins;
}

export function generateScatterFromBins(
  item: StatisticsSummaryItem,
  idx: number,
  chart: Highcharts.Chart,
  isOverlay: boolean,
) {
  const scatterPointRadius = isOverlay ? SCATTER_DOT_OVERLAY_RADIUS : SCATTER_DOT_RADIUS;
  const scatterRadiusWithSpacing = scatterPointRadius + 1;
  const maxPointsOnLane = Math.round(getBoxWidth(chart, idx) / scatterRadiusWithSpacing);
  const xAxis = chart.xAxis[0];
  const boxCenter = xAxis.toPixels(idx);
  const yAxis = chart.yAxis[0];
  const binWidth = yAxis.toValue(scatterRadiusWithSpacing) - yAxis.toValue(0);
  const outlierBins = binValues(item.data.stats.outliers, binWidth);
  const scatterPoints: ChartPoint[] = [];
  let pixelX = -Infinity;
  outlierBins.forEach((bin) => {
    for (let i = 0; i < bin.values.length; i++) {
      const laneIndex = Math.floor(i / maxPointsOnLane);
      const multiplier = i % maxPointsOnLane;
      const middleOffset = i < 2 ? 1 : 0;
      pixelX =
        pixelX < boxCenter
          ? boxCenter + middleOffset + laneIndex + scatterRadiusWithSpacing * multiplier
          : boxCenter - middleOffset - laneIndex - scatterRadiusWithSpacing * multiplier;
      const x = xAxis.toValue(pixelX);
      const outlier = bin.values[i];

      scatterPoints.push({
        x,
        y: outlier.value,
        timestamp: outlier.timestamp,
        color: item.color,
        marker: { symbol: 'circle', radius: scatterPointRadius },
        uom: item.data.uom,
        itemId: item.id,
        selected: false,
      });
    }
  });
  return scatterPoints;
}

export function isChartKey(key: string): key is ChartKeys {
  return key in CHART_TABLE_COL_NAME;
}

export const getOrderedChartKeys = (keys: ChartKeys[]) => {
  return CHARTS_ORDER.filter((k) => keys.includes(k));
};

export const RUN_RULE_MARKER_BUDGET = 2000;

export const countRunRuleViolations = (data: ChartPoint[]): number =>
  data.reduce((count, point) => (point.marker?.rule ? count + 1 : count), 0);

/**
 * Highcharts draws one SVG element per enabled marker, which makes a chart with tens of thousands of run rule
 * violations slow enough to freeze the browser. When the violations exceed the budget, the violations are spread
 * evenly across the budget and only one violation per bucket keeps its marker drawn. Bucketing over the violations
 * rather than over every sample keeps the drawn markers spread across the violations even when they cluster in a
 * small span of a long series. Every violating point keeps its rule so the tooltip still reports it, and selected
 * points always keep their marker.
 */
export function thinRunRuleMarkers(
  data: ChartPoint[],
  budget = RUN_RULE_MARKER_BUDGET,
): { data: ChartPoint[]; thinned: boolean } {
  const violationCount = countRunRuleViolations(data);
  if (violationCount <= budget) {
    return { data, thinned: false };
  }

  const bucketSize = violationCount / budget;
  let violationsSeen = 0;
  let lastDrawnBucket = -1;
  const thinnedData = data.map((point) => {
    if (!point.marker?.rule) {
      return point;
    }
    const bucket = Math.floor(violationsSeen / bucketSize);
    violationsSeen += 1;

    if (point.selected || !point.marker.enabled) {
      return point;
    }
    if (lastDrawnBucket === bucket) {
      return { ...point, marker: { ...point.marker, enabled: false } };
    }
    lastDrawnBucket = bucket;
    return point;
  });

  return { data: thinnedData, thinned: true };
}

/**
 * Customize Highcharts tooltip behavior to delay showing the tooltip and remove it immediately.
 */
export function customizeTooltipDelay(this: Highcharts.Chart) {
  const SHOW_DELAY_MS = 400;
  const HIDE_DELAY_MS = 0;
  const tooltip = this.tooltip;
  const originalRefresh = tooltip.refresh.bind(tooltip);
  const originalHide = tooltip.hide.bind(tooltip);
  const originalDestroy = this.destroy.bind(this);
  let tooltipTimer: ReturnType<typeof setTimeout> | undefined;

  const clearTimer = () => clearTimeout(tooltipTimer);

  tooltip.refresh = (...args: Parameters<typeof originalRefresh>) => {
    clearTimer();
    tooltipTimer = setTimeout(() => originalRefresh(...args), SHOW_DELAY_MS);
  };
  tooltip.hide = () => {
    clearTimer();
    originalHide(HIDE_DELAY_MS);
  };
  this.destroy = () => {
    clearTimer();
    originalDestroy();
  };
}

function upsertCursorsForPoints(points: ChartPoint[], addCursor: PointSelectionActions['addCursor']) {
  const byXValue = new Map<number, Record<string, Array<{ yValue: number }>>>();
  for (const pt of points) {
    if (pt.timestamp) {
      const xValue = new Date(pt.timestamp).getTime();
      if (!byXValue.has(xValue)) {
        byXValue.set(xValue, {});
      }
      if (pt.itemId && pt.y !== null) {
        const pointsMap = byXValue.get(xValue)!;
        if (!pointsMap[pt.itemId]) pointsMap[pt.itemId] = [];
        pointsMap[pt.itemId].push({ yValue: pt.y });
      }
    }
  }
  byXValue.forEach((pointsMap, xValue) => addCursor(xValue, pointsMap as any, false));
}

export function getCursorNameFromPoint(pt: ChartPoint, calendarCursors: Cursor[]) {
  if (!pt.timestamp) return;
  const xValue = new Date(pt.timestamp).getTime();
  const cursor = calendarCursors.find((c) => c.xValue === xValue);
  return cursor?.name ?? '';
}

/** What a point selection changes. Workbench passes the real actions; content passes no-ops. */
export interface PointSelectionActions {
  addCursor: (xValue: number, points: Record<string, CursorPoint[]>, isCapsuleTime: boolean) => void;
  deleteCursor: (xValue: number, isCapsuleTime: boolean) => void;
  setSelectedPoints: SummaryReportActions['setSelectedPoints'];
}

export function onPointSelectionChange(
  chart: Highcharts.Chart,
  selectedPoints: ChartPoint[],
  itemIds: string[],
  { addCursor, deleteCursor, setSelectedPoints }: PointSelectionActions,
) {
  const newSelectedPoints = (chart.getSelectedPoints() as unknown as ChartPoint[]) ?? [];

  const deselectedPoints = _.differenceWith(
    selectedPoints,
    newSelectedPoints,
    (selectedPoint, newSelectedPoint) =>
      selectedPoint.itemId === newSelectedPoint.itemId && selectedPoint.timestamp === newSelectedPoint.timestamp,
  ).filter((point) => itemIds.includes(point.itemId));

  const otherChartsSelection = selectedPoints.filter((point) => !itemIds.includes(point.itemId));

  const newSelection = newSelectedPoints
    .filter((point) => !(point as unknown as Highcharts.Point & { destroyed?: boolean }).destroyed)
    .map((sp) => ({
      x: sp.x,
      y: sp.y,
      selected: true,
      timestamp: sp.timestamp,
      itemId: sp.itemId,
      color: sp.color,
      marker: sp.marker,
      uom: sp.uom,
    }));

  const remainingSelection: ChartPoint[] = [...otherChartsSelection, ...newSelection];
  const remainingXValues = new Set(
    remainingSelection.filter((p) => p.timestamp != null).map((p) => new Date(p.timestamp!).getTime()),
  );
  deselectedPoints.forEach((point) => {
    if (point.timestamp == null) return;
    const xValue = new Date(point.timestamp).getTime();
    if (!remainingXValues.has(xValue)) {
      deleteCursor(xValue, false);
    }
  });
  upsertCursorsForPoints(remainingSelection, addCursor);
  setSelectedPoints(remainingSelection);
}

export const debouncedOnPointSelectionChange = _.debounce(
  (chart: Highcharts.Chart, selectedPoints: ChartPoint[], itemIds: string[], actions: PointSelectionActions) => {
    onPointSelectionChange(chart, selectedPoints, itemIds, actions);
  },
  0,
);

export function formatIso8601Date(timestamp: string, timezone: string | null = null, showTimezone = true): string {
  const tz = timezone ? timezone : sqWorksheetStore.timezone.name;
  const dateMoment = moment(timestamp).tz(tz);
  let formattedDate = dateMoment?.format(TIME_FORMAT_NUMERICAL_SHORT);
  if (showTimezone) {
    const timezoneAbbreviation = sqTimezones.getAbbreviatedDisplay(tz, dateMoment);
    formattedDate += ` ${timezoneAbbreviation}`;
  }
  return formattedDate;
}

/**
 * Extracts and returns valid run rules from a statistics summary item.
 * Valid rules are those that exist in the RUN_RULES_MARKER_MAP.
 *
 * @param item - The statistics summary item containing SPC charts with rules
 * @returns Array of valid rule names, sorted alphabetically
 */
export function getValidRunRules(item?: StatisticsSummaryItem): string[] {
  const rules = Array.from(
    new Set(Object.values(item?.data?.spcCharts || {}).flatMap((chart: SpcChart) => chart?.rules || [])),
  ).sort();
  return rules.filter((rule) => RUN_RULES_MARKER_MAP[rule as keyof typeof RUN_RULES_MARKER_MAP] !== undefined);
}
